import React from "react";
import { Box, Typography, Card, CardContent, CardMedia } from "@mui/material";
import Grid from "@mui/material/Grid2";

interface CardProps {
  image: string;
  title: string;
  description: string;
}

interface FeatureSectionProps {
  sectionText: string;
  headerText: string;
  subText: string;
  cards: CardProps[];
}

const FeatureSection: React.FC<FeatureSectionProps> = ({
  sectionText,
  headerText,
  subText,
  cards,
}) => {
  return (
    <Box
      sx={{
        py: { xs: 4, md: 8 },
        px: { xs: 2, md: 4 },
        backgroundColor: "#070a10",
        width: "100%",
      }}
    >
      <Box
        sx={{
          textAlign: "left",
          marginBottom: "3rem",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 0, md: 2 },
        }}
      >
        <Typography
          variant="overline"
          sx={{
            fontWeight: 600,
            color: "#a78bfa",
            marginBottom: "1rem",
            display: "block",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          {sectionText}
        </Typography>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            marginBottom: "1rem",
            color: "#fff",
            fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
          }}
        >
          {headerText}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "#94a3b8",
            lineHeight: "1.8",
            maxWidth: "1200px",
          }}
        >
          {subText}
        </Typography>
      </Box>

      <Grid
        container
        spacing={4}
        marginTop={4}
        sx={{
          justifyContent: "center",
          maxWidth: 1280,
          mx: "auto",
        }}
      >
        {cards.map((card, index) => (
          <Grid
            key={index}
            size={{ xs: 12, sm: 6, md: 3 }}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Card
              sx={{
                width: "100%",
                maxWidth: "350px",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
                borderRadius: "16px",
                overflow: "hidden",
                transition: "transform 0.2s ease-in-out, border-color 0.2s",
                border: "1px solid rgba(51, 65, 85, 0.8)",
                bgcolor: "rgba(15, 23, 42, 0.9)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  borderColor: "rgba(139, 92, 246, 0.45)",
                },
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image={card.image}
                alt={card.title}
                sx={{
                  objectFit: "contain",
                  bgcolor: "#0b0f17",
                }}
              />
              <CardContent
                sx={{
                  padding: "1.5rem",
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: "0.5rem",
                    color: "#fff",
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#94a3b8",
                    lineHeight: "1.6",
                  }}
                >
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default FeatureSection;
